<template>
  <RTLProvider>
    <router-view />
    <widget-container-modal />
  </RTLProvider>

  <!-- NotificationPopup moved to Private.vue -->
</template>

<script lang="ts">
import { useStockSocket } from "@/composables/stock_socket.composable";
import { usePricingSocket } from "@/composables/pricing_socket.composable";
import { initializeApp, getApps } from "firebase/app";
import { container } from "jenesius-vue-modal";
import {
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  defineAsyncComponent,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { provideToast, TYPE } from "vue-toastification";
import { useFCMNotification } from "./composables/fcm.notification.composable";
import { ResendVerificationDTO } from "./helpers/models/resendverification.dto";
import { RouteEnum } from "./router/routeNames";
import firebaseService from "./services/firebase.service";
import httpService from "./services/http.service";
import { isKeycloakEnabled, setPostLoginRedirect } from "./services/keycloak.service";
import { hasKeycloakConfig } from "@/lib/keycloak";
import storageService from "./services/storage.service";
import { requiresProfileOnboarding } from "./helpers/profileOnboarding";
import utilService from "./services/util.service";
import { useProfileScreenComposable } from "./views/private/profile-screen/composables/profile.screen.composable";
import { useDeviceInfo } from "./composables/deviceinfo.composable";
import { useCommonKey } from "./composables/commonkey.composable";
import RTLProvider from '@/components/layout/RTLProvider.vue'
// import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue'


// Notifications moved to Private.vue for authenticated users only

export default defineComponent({
  components: { WidgetContainerModal: container },
  setup() {
    provideToast({ timeout: 3000 });
    useDeviceInfo();
    const router = useRouter();
    const { connectToSocket, disconnectFromSocket } = useStockSocket();
    const {
      connectToSocket: pricingConnection,
      disconnectFromSocket: disconnectPricing,
    } = usePricingSocket();
    // Notification store moved to Private.vue
    const route = useRoute();
    const { registerForNotification, registerServiceWorker } =
      useFCMNotification();
    const backgroundCont = ref();
    const { commonKey } = useCommonKey();
    const getNotificationPermission = async () => {
      try {
        if ("Notification" in window) {
          var permission = await Notification.requestPermission();
        }
      } catch (e) { }
    };

    // Ensure Firebase exists before child components (Header messenger) mount.
    if (!getApps().length) {
      initializeApp(utilService.FirebaseConfig);
    }

    onMounted(async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const logout = urlParams.get("logout");
        if (logout === "true") {
          storageService.RemoveAllProperties();
          urlParams.delete("logout");
          const queryString = urlParams.toString();
          const newUrl =
            window.location.pathname +
            (queryString ? `?${queryString}` : "") +
            window.location.hash;
          window.history.replaceState({}, "", newUrl);
          router.push({ name: "signin" });
          return;
        }
        registerServiceWorker();
        const popover = document.querySelector(".el-popover");
        if (popover) {
          popover.addEventListener("click", (event) => {
            event.stopPropagation();
          });
        }
        if (!getApps().length) {
          initializeApp(utilService.FirebaseConfig);
        }
        connectToSocket();
        pricingConnection();
        if (
          location.pathname.toLowerCase().includes("resetpassword") ||
          location.pathname.toLowerCase().includes("verifyemail")
        ) {
          storageService.RemoveAllProperties();
          return;
        }
        if (location.pathname.toLowerCase().includes("twitter")) {
          return;
        }
        if (location.pathname.toLowerCase().includes("_linked_in")) {
          return;
        }
        if (location.pathname.toLowerCase().includes("_reddit")) {
          return;
        }
        try {
          await getNotificationPermission();
        } catch (e) { }
        var token = storageService.getToken();
        var user = storageService.getUser();
        if (token != null && user != null && user._id) {
          storageService.setBackground(user);
          // Init productier widget on app load if user is logged in
          // utilService.mountProductierWidget(user);
          const shouldEnforceLegacyEmailLock =
            !isKeycloakEnabled() && !hasKeycloakConfig();
          if (
            shouldEnforceLegacyEmailLock &&
            user.isEmailVerified == false &&
            user.email &&
            user.email.length
          ) {
            setPostLoginRedirect(
              `${window.location.pathname}${window.location.search}${window.location.hash}`
            );
            storageService.RemoveAllProperties();
            storageService.resendVerifyEmailDTO = new ResendVerificationDTO(
              user.email!,
              user.fullName!
            );
            router.push({ name: "locked" });
            return;
          }
          if (
            user.isEmailVerified != true &&
            user.isPhoneVerified == false &&
            user.phone &&
            user.phone.length
          ) {
            router.push({
              name: "otpVerification",
              query: { backToLogin: "1" },
            });
            return;
          }
          await firebaseService.anonymousLogin();
          if (!user.isGuestUser) {
            try {
              let userResponse = await httpService.getProfileData(
                user.userName
              );
              if (userResponse) {
                storageService.setUser(userResponse.data.data);
              }
              commonKey.value = true;
              updateLoggedInUser(user._id);
              await getNotificationPermission();
              await registerForNotification();
            } catch (e) { }
            const currentUser = storageService.getUser();
            const { loadProfileScreen, updateUserStats, isEditableView } =
              useProfileScreenComposable();
            isEditableView.value = false;
            if (!route.params.username) {
              loadProfileScreen(currentUser.userName!);
            }
            updateUserStats(currentUser.stats);
            if (requiresProfileOnboarding(currentUser)) {
              router.replace({ name: "createProfile" });
              return;
            }
          }
          if (
            !route.meta.requiresAuth &&
            route.name != RouteEnum.profile &&
            !location.href.includes("profile/user")
          ) {
          }
          return;
        } else {
          // Hash history: real path is in route.path (e.g. /account-banned), not location.pathname.
          if (route.name === "accountBanned" || route.name === "accountDeleted") {
            return;
          }
          const path = location.pathname.toLowerCase();
          const routePath = (route.path || "").toLowerCase();
          const isPublicAuthPath =
            routePath.startsWith("/signin") ||
            path.startsWith("/signin") ||
            routePath.startsWith("/signup") ||
            path.startsWith("/signup") ||
            routePath.startsWith("/forget-password") ||
            path.startsWith("/forget-password") ||
            routePath.startsWith("/resetpassword") ||
            path.startsWith("/resetpassword") ||
            routePath.startsWith("/verifyemail") ||
            path.startsWith("/verifyemail") ||
            routePath.startsWith("/otp-verification") ||
            path.startsWith("/otp-verification") ||
            routePath.startsWith("/approveaccess") ||
            path.startsWith("/approveaccess") ||
            routePath.startsWith("/locked") ||
            path.startsWith("/locked") ||
            routePath.startsWith("/account-banned") ||
            path.startsWith("/account-banned") ||
            routePath.startsWith("/account-deleted") ||
            path.startsWith("/account-deleted");
          // Unauthenticated /profile/user/:username is handled in ProfileScreen (redirect to public /u/:username).
          const shouldForceSignin =
            (route.meta.requiresAuth || !route.name) &&
            !path.includes("user") &&
            !path.includes("public") &&
            !path.startsWith("/u/") &&
            !routePath.includes("/u/") &&
            !isPublicAuthPath;
          if (shouldForceSignin) {
          router.push({ name: "signin" });
          }
        }
      } catch (e: any) {
        utilService.showToast(e.message, TYPE.ERROR);
      }
    });

    const updateLoggedInUser = (userId: string) => {
      httpService
        .getUserById(userId)
        .then((res) => {
          if (res.isSuccess) {
            storageService.setUser(res.data);
          }
        })
        .catch((e) => { });
    };

    onBeforeUnmount(() => {
      disconnectFromSocket();
      disconnectPricing();
    });

    return {
      backgroundCont,
      userInfo: storageService.getUser(),
    };
  },
});
</script>
<style lang="scss">
.background_container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  background-size: 100% 100%;
  background-repeat: no-repeat;

  +* {
    z-index: 10;
  }
}
</style>
